const BOARD_DATA_BASE_URL = 'https://abet-board-data.abetco.workers.dev'
const SOURCE_TIMEOUT_MS = 20_000

const MARKET_CODES = {
  Moneyline: 'ML',
  Spread: 'SPREAD',
  'Run Line': 'SPREAD',
  'Puck Line': 'SPREAD',
  Total: 'TOTAL',
}

function numberOrNull(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function lineToken(line) {
  return line == null || line === '' ? 'None' : String(line)
}

async function fetchBoardArtifact(name) {
  const response = await fetch(`${BOARD_DATA_BASE_URL}/data/${name}`, {
    cache: 'no-store',
    signal: AbortSignal.timeout(SOURCE_TIMEOUT_MS),
  })
  if (!response.ok) throw new Error(`${name} unavailable (HTTP ${response.status})`)
  return response.json()
}

export async function fetchMovementBundle() {
  const [board, history, openers, closers, release] = await Promise.all([
    fetchBoardArtifact('cat_game-lines.json'),
    fetchBoardArtifact('crossbook_history.json'),
    fetchBoardArtifact('crossbook_openers.json'),
    fetchBoardArtifact('crossbook_closers.json'),
    fetchBoardArtifact('release_manifest.json'),
  ])
  return { board, history, openers, closers, release }
}

export function buildOfferHistoryKey(row) {
  return [row.book, row.event, row.bet_type, row.selection, lineToken(row.line)].join('|')
}

export function parseOfferHistoryKey(key) {
  const [book = '', event = '', betType = '', selection = '', rawLine = 'None'] = String(key).split('|')
  return {
    book,
    event,
    betType,
    selection,
    line: rawLine === 'None' || rawLine === 'null' || rawLine === '' ? null : numberOrNull(rawLine) ?? rawLine,
  }
}

export function epochMinuteToIso(epochMinute) {
  const minute = numberOrNull(epochMinute)
  return minute == null ? null : new Date(minute * 60_000).toISOString()
}

export function normalizeMovementMarket(betType) {
  return MARKET_CODES[betType] || String(betType || '').toUpperCase()
}

function addPoint(points, minute, odds, source) {
  const timestamp = epochMinuteToIso(minute)
  const americanOdds = numberOrNull(odds)
  if (!timestamp || americanOdds == null) return
  points.set(`${minute}|${americanOdds}`, { timestamp, americanOdds, source })
}

function eventTeams(eventName) {
  const [awayTeam = '', homeTeam = ''] = String(eventName || '').split(' @ ', 2)
  return { awayTeam, homeTeam }
}

function eventSortValue(game) {
  const timestamp = Date.parse(`${game.date || ''} ${game.time || ''}`)
  return Number.isFinite(timestamp) ? timestamp : Date.parse(`${game.date || ''}T12:00:00Z`) || 0
}

export function buildMovementCatalog(bundle) {
  const boardRows = Array.isArray(bundle?.board?.rows) ? bundle.board.rows : []
  const history = bundle?.history && typeof bundle.history === 'object' ? bundle.history : {}
  const openers = bundle?.openers && typeof bundle.openers === 'object' ? bundle.openers : {}
  const closers = bundle?.closers && typeof bundle.closers === 'object' ? bundle.closers : {}
  const capturedAt = bundle?.release?.captured_utc || null
  const capturedMinute = capturedAt ? Math.floor(Date.parse(capturedAt) / 60_000) : null
  const gameMap = new Map()
  const series = []

  boardRows
    .filter((row) => (row.period || 'Game') === 'Game')
    .forEach((row) => {
      const gameId = `${row.event}|${row.et_date}`
      if (!gameMap.has(gameId)) {
        const teams = eventTeams(row.event)
        gameMap.set(gameId, {
          id: gameId,
          event: row.event,
          date: row.et_date || null,
          time: row.et_time || null,
          league: String(row.league || 'OTHER').toUpperCase(),
          awayTeam: teams.awayTeam,
          homeTeam: teams.homeTeam,
          markets: new Set(),
          books: new Set(),
        })
      }

      const game = gameMap.get(gameId)
      const marketCode = normalizeMovementMarket(row.bet_type)
      game.markets.add(marketCode)
      game.books.add(row.book)

      const key = buildOfferHistoryKey(row)
      const opener = Array.isArray(openers[key]) ? openers[key] : null
      const closer = Array.isArray(closers[key]) ? closers[key] : null
      const openerDate = opener?.[2] || null
      const dateMatches = !openerDate || !row.et_date || openerDate === row.et_date
      const rawHistory = dateMatches && Array.isArray(history[key]) ? history[key] : []
      const points = new Map()

      if (dateMatches && opener) addPoint(points, opener[0], opener[1], 'opener')
      rawHistory.forEach((point) => {
        if (Array.isArray(point)) addPoint(points, point[0], point[1], 'history')
      })
      if (capturedMinute != null) addPoint(points, capturedMinute, row.american, 'current-release')

      const orderedPoints = Array.from(points.values())
        .sort((left, right) => Date.parse(left.timestamp) - Date.parse(right.timestamp))
      const currentOdds = numberOrNull(row.american)
      const openingOdds = dateMatches && opener ? numberOrNull(opener[1]) : orderedPoints[0]?.americanOdds ?? null

      series.push({
        key,
        gameId,
        bookmaker: row.book,
        event: row.event,
        date: row.et_date || null,
        time: row.et_time || null,
        league: game.league,
        marketCode,
        marketLabel: row.bet_type,
        selection: row.selection,
        line: row.line ?? null,
        isPrimary: row.is_primary !== false,
        currentOdds,
        openingOdds,
        closingOdds: closer ? numberOrNull(closer[1]) : null,
        points: orderedPoints,
      })
    })

  const games = Array.from(gameMap.values())
    .map((game) => ({
      ...game,
      markets: Array.from(game.markets).sort(),
      books: Array.from(game.books).sort(),
      bookCount: game.books.size,
    }))
    .sort((left, right) => eventSortValue(left) - eventSortValue(right) || left.event.localeCompare(right.event))

  return {
    games,
    series,
    proof: {
      releaseId: bundle?.release?.release_id || null,
      capturedAt,
      generatedAt: bundle?.release?.generated_utc || null,
      boardRows: boardRows.length,
      historyOffers: Object.keys(history).length,
      openerOffers: Object.keys(openers).length,
      closerOffers: Object.keys(closers).length,
      gameCount: games.length,
      bookCount: Array.isArray(bundle?.release?.game_line_books)
        ? bundle.release.game_line_books.length
        : new Set(boardRows.map((row) => row.book)).size,
    },
  }
}

export function impliedProbability(americanOdds) {
  const odds = numberOrNull(americanOdds)
  if (odds == null) return null
  if (odds >= 100) return 100 / (odds + 100)
  if (odds <= -100) return Math.abs(odds) / (Math.abs(odds) + 100)
  return null
}
